// @ts-strict-ignore
import React, { useCallback, useMemo } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Box,
  Button,
  Callout,
  DataTable,
  DataTableCellVariants,
  DataTableHeaderProps,
  DataTableRowData,
  EmptyState,
  NoData,
  RequestError,
  RowsSkeletonLoader,
  useSidePanel,
} from '@node-space/ui-components'
import { ExternalLink } from 'components/ExternalLink/ExternalLink'
import { DateFormat } from 'constants/General'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useApprovalsContext } from 'hooks/context/useApprovalsContext'
import { ApprovalTableTypes } from 'pages/Approvals/types'
import { getTableHeaders } from 'pages/Approvals/util'
import { ApprovalStatuses } from 'types/approvals'
import { SidePanelSummary } from '../SidePanel/SidePanelSummary'

export const PendingTransactions = () => {
  const { t } = useTranslation()

  const {
    pendingApprovalDetails: details,
    permissions,
    isFetching,
    isError,
    approvalsConfig,
  } = useApprovalsContext()
  const { pendingApprovals = [], hasPendingApprovals = false, fetchNextPendingPage } = details
  const {
    allApprovalFlowsAreDisabled = true,
    handleTableRowCheck = () => {},
    handleManageApproversModal = () => {},
    handleApprovalPreferenceSettingsModal = () => {},
  } = approvalsConfig ?? {}

  const {
    isSidePanelOpen: isPendingApprovalDetailsOpen,
    sidePanelData: pendingApprovalDetails,
    paging,
    setIsSidePanelOpen,
    updateSidePanel,
  } = useSidePanel(pendingApprovals)

  const openSidePanelSummary = (index: number) => {
    updateSidePanel(index)
    setIsSidePanelOpen(true)
  }

  const columns: DataTableHeaderProps[] = useMemo(() => {
    return getTableHeaders(t, ApprovalTableTypes.PENDING)
  }, [t])

  const tableData: DataTableRowData[] = useMemo(() => {
    if (!pendingApprovals) return []

    return pendingApprovals?.map((approvalTransaction, index) => {
      return {
        id: approvalTransaction?.correlationKey,
        testid: approvalTransaction?.correlationKey,
        onRowClick: () => {
          openSidePanelSummary(index)
        },
        cells: [
          {
            key: 'payment',
            variant: DataTableCellVariants.TEXT,
            Avatar: <Avatar size="small" name={approvalTransaction?.currency?.code} />,
            Text: `${approvalTransaction?.amount} ${approvalTransaction?.currency?.code}`,
            BottomText: approvalTransaction?.walletName,
          },
          {
            key: 'to',
            variant: DataTableCellVariants.TEXT,
            Text:
              approvalTransaction?.beneficiaryDetails?.accountNumber ||
              approvalTransaction?.beneficiaryDetails?.destinationWallet,
            BottomText: approvalTransaction?.beneficiaryDetails?.beneficiaryName,
          },
          {
            key: 'approvalsInitiatedBy',
            variant: DataTableCellVariants.TEXT,
            Text: approvalTransaction?.initiatedBy || approvalTransaction?.initiatedByEmail,
            BottomText: dayjs(approvalTransaction?.actionedAt).format(
              DateFormat.D_MMM_YYYY_AT_HH_MM
            ),
          },
          {
            key: 'approvalsRequired',
            variant: DataTableCellVariants.TEXT,
            Text: approvalTransaction?.approvalsCounter
              ? `${approvalTransaction?.approvalsCounter?.approvalsRequired - approvalTransaction?.approvalsCounter?.approvalsAcquired}`
              : '-',
          },
        ],
      }
    })
  }, [pendingApprovals])

  const LearnMoreLink = useMemo(() => {
    return <ExternalLink href={`${process.env.ABOUT_APPROVAL_LINK}`}>{t('learnMore')}</ExternalLink>
  }, [t])

  const PreferencesButton = useMemo(() => {
    return (
      <Button
        disabled={!permissions?.canEdit}
        onClick={() => handleApprovalPreferenceSettingsModal({ isVisible: true })}
      >
        {t('approvals.enableAndEditPreferences')}
      </Button>
    )
  }, [permissions?.canEdit])

  const ManageApproversButton = useMemo(() => {
    return !allApprovalFlowsAreDisabled && !hasPendingApprovals ? (
      <Button
        variant="secondary"
        disabled={!permissions?.canEdit}
        onClick={() => handleManageApproversModal({ isVisible: true })}
      >
        {t('approvals.manageApprovers')}
      </Button>
    ) : undefined
  }, [allApprovalFlowsAreDisabled, hasPendingApprovals, permissions?.canEdit])

  const handleTableRowSelection = useCallback(
    (selectedRows: DataTableRowData[]) => {
      const selectedTableData = selectedRows.reduce((tableRows, current) => {
        const selectedTx = pendingApprovals?.find(tx => tx?.correlationKey === current?.id)

        return [...tableRows, ...(selectedTx ? [selectedTx] : [])]
      }, [])

      handleTableRowCheck(selectedTableData)
    },
    [pendingApprovals]
  )

  return (
    <Box paddingB={16}>
      {allApprovalFlowsAreDisabled && hasPendingApprovals && !isFetching && (
        <Box paddingB={16}>
          <Callout variant="warning">{t('approvals.disableApprovalCalloutDisclaimer')}</Callout>
        </Box>
      )}

      {isFetching && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

      {!isFetching && isError && <RequestError />}

      {!isFetching && !isError && !hasPendingApprovals && allApprovalFlowsAreDisabled && (
        <EmptyState
          testId="empty-state"
          title={t('approvals.disabledApprovalsHeadline')}
          lottieName="not-verified"
          description={
            <>
              {`${t('approvals.disabledApprovalsDescription')} `}
              {LearnMoreLink}
            </>
          }
          PrimaryButton={PreferencesButton}
          SecondaryButton={ManageApproversButton}
        />
      )}

      {!isFetching && !isError && !hasPendingApprovals && !allApprovalFlowsAreDisabled && (
        <NoData
          dataName="transactions pending approval"
          description={
            <>
              {`${t('approvals.enabledAndNoPendingApprovalsDescription')} `}
              {LearnMoreLink}
            </>
          }
          PrimaryButton={PreferencesButton}
          SecondaryButton={ManageApproversButton}
        />
      )}

      {!isFetching && !isError && hasPendingApprovals && (
        <>
          <DataTable
            name="pending-transactions-table"
            headers={columns}
            data={tableData}
            isSelectable={true}
            onTableRowSelection={handleTableRowSelection}
          />

          {fetchNextPendingPage && (
            <Box flex justify="center" className="mt-6">
              <Button onClick={fetchNextPendingPage}>{t('approvals.loadMore')}</Button>
            </Box>
          )}

          {pendingApprovalDetails && (
            <SidePanelSummary
              isOpen={isPendingApprovalDetailsOpen}
              paging={paging}
              onClose={() => setIsSidePanelOpen(false)}
              transactionDetails={pendingApprovalDetails}
              tab={ApprovalStatuses.PENDING}
            />
          )}
        </>
      )}
    </Box>
  )
}
